import React from 'react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import { MoonStar, Clock, BedDouble } from 'lucide-react'
import type { SharedProps } from '@/types/shared'

/**
 * The ninth guide — Last-Minute Clearance (docs/plans/2026-08-13-last-minute-clearance.md §7).
 *
 * WRITTEN FOR A HOTELIER, NOT AN ANALYST. Rooms and hours, never ratios, thresholds or
 * "signals" — the module exists because a correctly-behaving engine LOOKED asleep, and a guide
 * that explains it in engine vocabulary would repeat the original failure in prose.
 *
 * The illustrated line below is a WORKED EXAMPLE for one room on one evening, not the reader's own
 * rule. Their own line is on their Clearance page, walked by the engine from their own rooms,
 * prices and closing time — this page never implies otherwise.
 */

const EXAMPLE_STEPS = [
  { when: '5pm', price: '£95', note: '4 rooms left and 4 hours to sell them — the first, gentlest step' },
  { when: '7pm', price: '£85', note: 'still 4 rooms, half the time left, so the steps get bigger' },
  { when: '8pm', price: '£77', note: 'an hour of selling left, and the room is £77 or your minimum' },
]

export default function ClearanceGuide({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Last-Minute Clearance"
      subtitle="Sell the last few rooms on the night, on a line you can read in advance."
    >
      <GuideSection title="What it does">
        <p className="text-sm leading-relaxed text-muted-foreground">
          A room that goes empty tonight earns nothing. It cannot be sold tomorrow — the night is
          gone. Clearance is the one rule that lowers a price for that reason and no other.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Close to arrival, if a room still has units unsold and the night is heading for empty
          rooms, the engine steps the price down once an hour — gently while there is still time to
          sell, harder as the evening runs out and the rooms are still there. The{' '}
          <strong className="text-foreground">
            price goes back to normal when the rooms are gone — or when the night stops heading for
            empty rooms
          </strong>
          . One sale with more rooms still going empty keeps the lower price working; when the last
          room goes, the price is back to normal straight away, so a late cancellation reopens at
          your full rate.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          It is switched off until you turn it on, one property at a time, on your Clearance page
          under Pricing Rules.
        </p>
      </GuideSection>

      <GuideSection title="You can read tonight's prices now">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Your Clearance page shows the worst case in your own money: what the room will be asking
          at each hour tonight <strong className="text-foreground">if nothing sells</strong>, down to
          the point it reaches your own minimum, as that minimum stands today. Prices follow that
          line or better — every sale holds the next step for an hour, and selling out ends it
          immediately. So you can look at it now and say no before it happens.
        </p>

        <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-border/40 bg-amber-50/60 px-5 py-2.5">
            <MoonStar className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-xs font-semibold text-foreground">An example evening</span>
            <span className="text-[11px] text-muted-foreground">
              a £100 room, 4 of 10 unsold, last bookings around 9pm — if nothing sells
            </span>
          </div>
          <div className="divide-y divide-border/40">
            {EXAMPLE_STEPS.map((step) => (
              <div key={step.when} className="flex items-baseline gap-3 px-5 py-3">
                <Clock className="h-3.5 w-3.5 shrink-0 translate-y-0.5 text-muted-foreground/60" />
                <span className="w-56 shrink-0 text-[13px] font-medium text-foreground">{step.when}</span>
                <span className="w-20 shrink-0 text-[13px] font-semibold tabular-nums text-amber-700">{step.price}</span>
                <span className="text-xs text-muted-foreground">{step.note}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border/30 bg-muted/20 px-5 py-2">
            <p className="text-[11px] text-muted-foreground">
              Your own line is on your Clearance page — the engine works the closing time out from
              when your own late bookings actually arrive, and you can pin it yourself. Each step
              takes between 5% and 10% off the engine's price for that night: the more rooms left
              and the less time to sell them, the bigger the next one. The price on the shelf can
              move further than that in one step, because the engine's own price for the night
              moves as well.
            </p>
          </div>
        </div>

        <p className="text-sm leading-relaxed text-muted-foreground">
          The discount applies to the room and anything linked to it, so a ladder stays a ladder: if
          your superior room is normally £15 above your standard, it still is.
        </p>
      </GuideSection>

      <GuideSection title="What it never does">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The list is short and none of it has exceptions.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Never goes below your floor.</strong> Your minimum
            room rate — and your price floor, if you use one — hold exactly as they always do. To let
            a price go lower you change the floor, not this rule; and a floor you raise mid-evening
            takes a cleared price back up with it on the next run.
          </li>
          <li>
            <strong className="text-foreground">Never touches a price you set by hand.</strong> A
            room with your own price on it is left alone completely.
          </li>
          <li>
            <strong className="text-foreground">Never runs after your last bookings hour.</strong>{' '}
            The steps stop when your late demand does, so the lowest price gets real exposure rather
            than sitting on an empty night at midnight.
          </li>
          <li>
            <strong className="text-foreground">Only ever steps down.</strong> Clearance cannot
            raise a price, and while a night is clearing it never gives a step back — even if the
            engine's own price for that night rises in the meantime. Two of your own settings still
            outrank it: change the room's minimum rate, or set the price yourself, and the price
            moves there on the next run, up or down. Prices rising into a busy night is a different
            part of the engine.
          </li>
          <li>
            <strong className="text-foreground">Never discounts a room nobody can book.</strong> If
            the room is stopped, closed to arrival, or your minimum stay means tonight cannot be
            bought on its own, there is nothing to clear and it does not fire.
          </li>
          <li>
            <strong className="text-foreground">Never discounts a room that is not going empty.</strong>{' '}
            Rooms that reliably sell out on the day at your normal price are left alone — clearance
            is for spoilage, not for giving away rooms you would have sold anyway.
          </li>
        </ul>
      </GuideSection>

      <GuideSection title="If a room needs clearing too often, it tells you">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Your Clearance page counts how many of the last 30 nights each room has cleared. Past the
          number you set — eight by default — the room is flagged as{' '}
          <strong className="text-foreground">clearing often</strong>, with the count beside it and a
          link straight to where its price is set.
        </p>
        <div className="flex gap-3 rounded-lg border border-amber-200/70 bg-amber-50/60 px-4 py-3">
          <BedDouble className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-sm leading-relaxed text-muted-foreground">
            A room needing to be cleared 8 nights in 30 is not having last-minute nights — it is
            priced too high every day of the week. Lower its offset or its base rate and it stops
            needing rescue; letting clearance go deeper would only hide the reason.
          </p>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The rule never stops itself. A flagged room keeps clearing while you decide what to do —
          the night in front of it is still heading for empty rooms, and an empty room earns nothing
          whatever the count says. It is a recommendation; the decision is yours.
        </p>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            Read the line on your Clearance page before you switch the rule on, and check that the
            price at the bottom of it is one you would genuinely rather have than an empty room.
          </GuideTip>
          <GuideTip type="do">
            If you want it to stop sooner than your minimum room rate does, set "never deeper than"
            on your Clearance page. Your minimum still holds either way.
          </GuideTip>
          <GuideTip type="do">
            Check your minimum room rate first. It is what stops a clearance step going anywhere you
            do not want it to, and it is the only thing that does.
          </GuideTip>
          <GuideTip type="dont">
            Do not use clearance to fix a room that sits half empty all month. Clearance salvages the
            odd night; a room that is always empty needs its price looking at.
          </GuideTip>
          <GuideTip type="dont">
            Do not run a blanket last-minute promotion across the hotel at the same time. Two
            discounts on the same room stack, and the second one is spending money the first had
            already done the job with.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'price-guardrails', title: 'Price Guardrails' },
          { slug: 'demand-response', title: 'Demand Response' },
          { slug: 'overrides', title: 'Overrides' },
          { slug: 'history', title: 'History' },
        ]}
      />
    </GuideLayout>
  )
}
